import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { supabase } from '../lib/supabase';

export interface SharedView {
  id: string;
  name: string;
  description?: string;
  viewConfig: Record<string, any>;
  createdByEmail?: string;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ViewTemplate {
  id: string;
  name: string;
  description?: string;
  templateConfig: Record<string, any>;
  isDefault: boolean;
  usageCount: number;
  createdAt: string;
}

export interface UserPermissions {
  canShareViews: boolean;
  canManageTemplates: boolean;
  isAdmin: boolean;
}

const defaultPermissions: UserPermissions = {
  canShareViews: true,
  canManageTemplates: false,
  isAdmin: false,
};

export interface SharedViewsState {
  sharedViews: SharedView[];
  viewTemplates: ViewTemplate[];
  userPermissions: UserPermissions;
  loading: boolean;
  error: string | null;
}

export interface UseSharedViewsReturn {
  state: SharedViewsState;
  refreshSharedViews: () => Promise<void>;
  refreshViewTemplates: () => Promise<void>;
  refreshUserPermissions: () => Promise<void>;
  createSharedView: (view: {
    name: string;
    description?: string;
    reportId: string;
    viewConfig: Record<string, any>;
  }) => Promise<{ success: boolean; sharedView?: SharedView; error?: string }>;
  updateSharedView: (
    id: string,
    updates: {
      name?: string;
      description?: string;
      viewConfig?: Record<string, any>;
    },
  ) => Promise<{ success: boolean; sharedView?: SharedView; error?: string }>;
  deleteSharedView: (id: string) => Promise<{ success: boolean; error?: string }>;
  trackViewUsage: (id: string, type: 'shared' | 'template') => Promise<void>;
  createViewTemplate: (template: {
    name: string;
    description?: string;
    reportId: string;
    templateConfig: Record<string, any>;
    isDefault?: boolean;
    sortOrder?: number;
  }) => Promise<{ success: boolean; template?: ViewTemplate; error?: string }>;
  updateViewTemplate: (
    id: string,
    updates: {
      name?: string;
      description?: string;
      templateConfig?: Record<string, any>;
      isDefault?: boolean;
      sortOrder?: number;
    },
  ) => Promise<{ success: boolean; template?: ViewTemplate; error?: string }>;
  deleteViewTemplate: (id: string) => Promise<{ success: boolean; error?: string }>;
}

export function useSharedViews(reportId: string = 'afl-daily'): UseSharedViewsReturn {
  const queryClient = useQueryClient();

  // Query: Fetch shared views (cached for 5 minutes)
  const {
    data: sharedViews = [],
    isLoading: sharedViewsLoading,
    error: sharedViewsError,
    refetch: refetchSharedViews,
  } = useQuery({
    queryKey: ['shared-views', reportId],
    queryFn: async () => {
      const response = await apiClient.get(`/api/prefs/shared-views?reportId=${reportId}`);
      const data = await response.json();
      return (data.sharedViews || []) as SharedView[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once for shared views
  });

  // Query: Fetch view templates (cached for 10 minutes - templates rarely change)
  const {
    data: viewTemplates = [],
    isLoading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates,
  } = useQuery({
    queryKey: ['view-templates', reportId],
    queryFn: async () => {
      const response = await supabase
        .from('view_templates')
        .select('*')
        .eq('report_id', reportId)
        .order('sort_order', { ascending: true });

      if (response.error) {
        console.error('Error fetching templates from Supabase:', response.error);
        return [];
      }

      const blockedNames = new Set([
        'Warranty Analysis',
        'Warranty',
        'Parts Analysis',
        'Labor Analysis',
      ]);

      return (response.data || [])
        .filter((row: any) => row.is_active !== false)
        .filter((row: any) => !blockedNames.has(row.name))
        .map((row: any) => ({
          id: row.id,
          name: row.name,
          description: row.description,
          templateConfig: row.template_config,
          isDefault: row.is_default,
          usageCount: row.usage_count || 0,
          createdAt: row.created_at,
        })) as ViewTemplate[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });

  // Query: Fetch user permissions (cached for 15 minutes - permissions rarely change)
  const {
    data: userPermissions = defaultPermissions,
    isLoading: permissionsLoading,
    error: permissionsError,
    refetch: refetchPermissions,
  } = useQuery({
    queryKey: ['user-permissions'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/api/prefs/shared-views/permissions');
        const data = await response.json();
        return (data.permissions || defaultPermissions) as UserPermissions;
      } catch (error) {
        console.error('Error fetching permissions:', error);
        return defaultPermissions;
      }
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: 1,
  });

  const loading = sharedViewsLoading || templatesLoading || permissionsLoading;
  const error = sharedViewsError || templatesError || permissionsError;

  // Mutation: Create shared view
  const createSharedViewMutation = useMutation({
    mutationFn: async (view: {
      name: string;
      description?: string;
      reportId: string;
      viewConfig: Record<string, any>;
    }) => {
      const response = await apiClient.post('/api/prefs/shared-views', view);
      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch shared views
      queryClient.invalidateQueries({ queryKey: ['shared-views', reportId] });
    },
  });

  const createSharedView = useCallback(
    async (view: {
      name: string;
      description?: string;
      reportId: string;
      viewConfig: Record<string, any>;
    }) => {
      try {
        const data = await createSharedViewMutation.mutateAsync(view);
        if (data.success) {
          return { success: true, sharedView: data.sharedView };
        } else {
          return { success: false, error: data.error || 'Failed to create shared view' };
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'Failed to create shared view' };
      }
    },
    [createSharedViewMutation],
  );

  // Mutation: Update shared view
  const updateSharedViewMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        name?: string;
        description?: string;
        viewConfig?: Record<string, any>;
      };
    }) => {
      const response = await apiClient.put(`/api/prefs/shared-views/${id}`, updates);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-views', reportId] });
    },
  });

  const updateSharedView = useCallback(
    async (
      id: string,
      updates: {
        name?: string;
        description?: string;
        viewConfig?: Record<string, any>;
      },
    ) => {
      try {
        const data = await updateSharedViewMutation.mutateAsync({ id, updates });
        if (data.success) {
          return { success: true, sharedView: data.sharedView };
        } else {
          return { success: false, error: data.error || 'Failed to update shared view' };
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'Failed to update shared view' };
      }
    },
    [updateSharedViewMutation],
  );

  // Mutation: Delete shared view
  const deleteSharedViewMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`/api/prefs/shared-views/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-views', reportId] });
    },
  });

  const deleteSharedView = useCallback(
    async (id: string) => {
      try {
        const data = await deleteSharedViewMutation.mutateAsync(id);
        if (data.success) {
          return { success: true };
        } else {
          return { success: false, error: data.error || 'Failed to delete shared view' };
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'Failed to delete shared view' };
      }
    },
    [deleteSharedViewMutation],
  );

  // Track view usage (fire and forget)
  const trackViewUsage = useCallback(async (id: string, type: 'shared' | 'template') => {
    try {
      await apiClient.post(
        `/api/prefs/shared-views/${type === 'template' ? 'templates' : ''}${type === 'template' ? '/' : ''}${id}/usage`,
        {},
      );
    } catch (error) {
      console.warn('Failed to track view usage:', error);
    }
  }, []);

  // Mutation: Create view template
  const createViewTemplateMutation = useMutation({
    mutationFn: async (template: {
      name: string;
      description?: string;
      reportId: string;
      templateConfig: Record<string, any>;
      isDefault?: boolean;
      sortOrder?: number;
    }) => {
      const response = await apiClient.post('/api/prefs/shared-views/templates', template);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['view-templates', reportId] });
    },
  });

  const createViewTemplate = useCallback(
    async (template: {
      name: string;
      description?: string;
      reportId: string;
      templateConfig: Record<string, any>;
      isDefault?: boolean;
      sortOrder?: number;
    }) => {
      try {
        const data = await createViewTemplateMutation.mutateAsync(template);
        if (data.success) {
          return { success: true, template: data.template };
        } else {
          return { success: false, error: data.error || 'Failed to create view template' };
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'Failed to create view template' };
      }
    },
    [createViewTemplateMutation],
  );

  // Mutation: Update view template
  const updateViewTemplateMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        name?: string;
        description?: string;
        templateConfig?: Record<string, any>;
        isDefault?: boolean;
        sortOrder?: number;
      };
    }) => {
      const response = await apiClient.put(`/api/prefs/shared-views/templates/${id}`, updates);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['view-templates', reportId] });
    },
  });

  const updateViewTemplate = useCallback(
    async (
      id: string,
      updates: {
        name?: string;
        description?: string;
        templateConfig?: Record<string, any>;
        isDefault?: boolean;
        sortOrder?: number;
      },
    ) => {
      try {
        const data = await updateViewTemplateMutation.mutateAsync({ id, updates });
        if (data.success) {
          return { success: true, template: data.template };
        } else {
          return { success: false, error: data.error || 'Failed to update view template' };
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'Failed to update view template' };
      }
    },
    [updateViewTemplateMutation],
  );

  // Mutation: Delete view template
  const deleteViewTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`/api/prefs/shared-views/templates/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['view-templates', reportId] });
    },
  });

  const deleteViewTemplate = useCallback(
    async (id: string) => {
      try {
        const data = await deleteViewTemplateMutation.mutateAsync(id);
        if (data.success) {
          return { success: true };
        } else {
          return { success: false, error: data.error || 'Failed to delete view template' };
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'Failed to delete view template' };
      }
    },
    [deleteViewTemplateMutation],
  );

  return {
    state: {
      sharedViews,
      viewTemplates,
      userPermissions,
      loading,
      error: error ? String(error) : null,
    },
    refreshSharedViews: () => refetchSharedViews().then(() => undefined),
    refreshViewTemplates: () => refetchTemplates().then(() => undefined),
    refreshUserPermissions: () => refetchPermissions().then(() => undefined),
    createSharedView,
    updateSharedView,
    deleteSharedView,
    trackViewUsage,
    createViewTemplate,
    updateViewTemplate,
    deleteViewTemplate,
  };
}
